<template>
  <ModalDialog
    :header-text="`Invoice ${transaction.DocumentNumber}`"
    contentClass="transaction-details-content"
    @close-modal="closeModal"
    open
  >
    <template v-slot:content>
      <div class="flex-container">
        <div class="balance-blocks-container">
          <div class="balance-block" v-if="originalAmount !== 0">
            Original Amount
            <span class="balance-text"> {{ CurrencyFormatter.format(originalAmount) }}</span>
          </div>
          <div class="balance-block" v-if="transaction.IsMemoInvoice !== true">
            Remaining Balance
            <span class="balance-text">
              {{ CurrencyFormatter.format(transaction.TransactionBalance) }}
            </span>
          </div>
          <Loader v-show="paymentsLoading" class="payments-loader" size="large" type="pulsing" />
          <div class="payments-grid-wrapper">
            <Grid
              :class="{ hidden: paymentsLoading }"
              class="payments-grid"
              :style="{ minWidth: `${gridMinWidth}px` }"
              :data-items="payments"
              :columns="columns"
              :scrollable="'none'"
            >
              <template v-slot:AmountAppliedCell="{ props: { dataItem, field } }">
                <td class="k-table-td">
                  <span v-if="dataItem.IsCleared">{{ CurrencyFormatter.format(dataItem[field]) }}</span>
                </td>
              </template>
              <template v-slot:AmountSubmittedCell="{ props: { dataItem, field } }">
                <td class="k-table-td">
                  <span v-if="!dataItem.IsCleared">{{ CurrencyFormatter.format(dataItem[field]) }}</span>
                </td>
              </template>
              <template v-slot:CustomerFeeWaivedCell="{ props: { dataItem, field } }">
                <td class="k-table-td">
                  <span v-if="dataItem[field]">Yes</span>
                </td>
              </template>
            </Grid>
          </div>
        </div>
      </div>
    </template>
  </ModalDialog>
</template>

<script setup lang="ts">
import { Grid, type GridColumnProps } from "@progress/kendo-vue-grid";
import { computed, onMounted, ref, watch } from "vue";
import ModalDialog from "@/shared/ui/modal/ModalDialog.vue";
import type { FinancialTransaction } from "@/Domain/FinancialTransaction";
import { CurrencyFormatter } from "@/shared/helpers/formatHelper";
import { useMainStore } from "@/stores/useMainStore";
import { api } from "@/api";
import type { FinancialPayment } from "@/Domain/FinancialPayment";
import { Loader } from "@progress/kendo-vue-indicators";

const main = useMainStore();

const props = defineProps<{
  transaction: FinancialTransaction;
}>();

const emit = defineEmits(["closeModal"]);

const paymentsLoading = ref(false);
const payments = ref<FinancialPayment[]>([]);

const staticColumns: GridColumnProps[] = [
  {
    title: "Transaction ID",
    field: "DocumentNumber",
    width: "150px",
  },
  {
    title: "Transaction Type",
    field: "DocumentType",
    width: "170px",
  },
  {
    title: "Transaction Date",
    field: "TransactionDate",
    type: "date",
    format: "{0:MM/dd/yyyy}",
    width: "130px",
  },
  {
    title: "Date Applied",
    field: "DateApplied",
    type: "date",
    format: "{0:MM/dd/yyyy}",
    width: "130px",
  },
  {
    title: "Amount Submitted",
    field: "AmountApplied",
    cell: "AmountSubmittedCell",
    type: "number",
    format: "{0:c}",
    width: "140px",
  },
];

const columns = computed(() => {
  const cols = [] as GridColumnProps[];
  if (props.transaction.IsMemoInvoice !== true)
    cols.push({
      title: "Amount Applied",
      field: "AmountApplied",
      cell: "AmountAppliedCell",
      type: "number",
      format: "{0:c}",
      width: "140px",
    });
  if (main.isEmulator)
    cols.push({
      title: "Fee Waived",
      field: "CustomerFeeWaived",
      cell: "CustomerFeeWaivedCell",
      width: "100px",
    });
  return [...staticColumns, ...cols];
});

const gridMinWidth = computed(() => columns.value.reduce((total, col) => total + parseInt(col.width as string, 10), 0));

const originalAmount = computed(() => {
  if (props.transaction.IsMemoInvoice === true) {
    return props.transaction.TransactionBalance;
  }
  return props.transaction.Debits >= props.transaction.Credits
    ? props.transaction.Debits
    : props.transaction.Credits * -1;
});

const getPaymentsAsync = async function () {
  if (paymentsLoading.value) return;
  try {
    paymentsLoading.value = true;
    let result = await api.get("GetFinancialPayments", {
      companyLogin: main.companyLogin,
      docNumber: props.transaction.DocumentNumber,
      docType: props.transaction.DocumentType,
    });
    if (result.success) {
      payments.value = result.response;
    } else {
      console.error("TransactionDetailsModal.vue: getPaymentsAsync(): resp = " + result);
    }
    console.debug(`TransactionDetailsModal.vue: getPaymentsAsync(), result:`, result);
    console.debug(`TransactionDetailsModal.vue: getPaymentsAsync(), payments:`, payments);
  } catch (err) {
    console.error("TransactionDetailsModal.vue: getAgingCustomerBalanceAsync(): catch error = " + err);
  } finally {
    paymentsLoading.value = false;
  }
};

const closeModal = function () {
  emit("closeModal");
};

watch(
  () => props.transaction,
  async () => {
    await getPaymentsAsync();
  }
);
onMounted(async () => {
  await getPaymentsAsync();
});
</script>

<style scoped lang="scss">
.flex-container {
  display: flex;
  flex-wrap: nowrap;
  margin-top: 10px;
  margin-bottom: 20px;

  .balance-blocks-container {
    display: flex;
    flex-direction: column;
    flex: 1;
    margin-right: 20px;
    min-width: 0;

    .balance-block {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 10px;
      font-size: 16px;
      font-weight: 200;
      color: #444;

      .balance-text {
        font-weight: 600;
      }
    }
  }

  .payments-loader {
    margin: 0 auto;
  }

  .payments-grid-wrapper {
    overflow-x: auto;
  }

  .hidden {
    opacity: 0;
    display: block !important;
  }
}

.flex__left {
  flex: 1;
  margin-right: 40px;
}

.flex__right {
  flex: 3;
}

.grid-container {
  overflow: auto;
  max-height: 440px;
  border: 1px solid #ddd;
}

.form-row {
  display: flex;
  margin-bottom: 10px;
  align-items: center;
}

.form-row label {
  font-weight: 200;
  text-align: right;
  min-width: 125px;
  margin-right: 20px;
}

td {
  font-size: 13px;
}
</style>

<style lang="scss">
.transaction-details-content {
  @include media-breakpoint-up(sm) {
    max-width: 90vw;
  }
}
</style>
